import { useEffect, useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, UPLOADS_BASE, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Upload, Pencil, Mail, Send, Scale } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  nom: "", type: "boutique", adresse: "", code_postal: "", ville: "", telephone: "", siret: "",
  tva_intracom: "", forme_juridique: "", capital_social: "", rcs_ville: "", tva_sur_debits: false,
  acquisition_date: "", usage_start_date: "", usage_start_justification: "",
  ticket_impression_auto: false, activite_exemptee_agec: false,
  vente_mode: "standard",
  caisse_mode: "unique", nombre_caisses: 2,
  smtp_host: "", smtp_port: "", smtp_secure: "starttls", smtp_user: "", smtp_password: "",
  smtp_from_email: "", smtp_from_name: "",
};

// Modèles d'affichage de l'onglet Vente (voir CaissePage.jsx et
// routers/shops_routes.py > VENTE_MODES) — le moteur d'encaissement lui-même
// (paiement, TVA, avoir, fidélité) reste identique quel que soit le modèle
// choisi, seule la façon de composer le panier change.
const VENTE_MODE_OPTIONS = [
  { value: "standard", label: "Standard", help: "Recherche et panier — le fonctionnement historique, adapté à une boutique avec beaucoup de références en stock." },
  { value: "grille_tactile", label: "Grille tactile par catégorie", help: "Gros boutons visuels par catégorie — adapté à une boulangerie, un snack, un nombre limité de références fixes." },
  { value: "vente_poids", label: "Vente au poids", help: "Prix calculé à partir d'un poids saisi — adapté à une boucherie, un primeur, une épicerie." },
  { value: "menus_formules", label: "Menus et formules", help: "Prix fixe regroupant plusieurs articles à TVA différente — adapté à la restauration rapide, une boulangerie-sandwicherie." },
  { value: "par_table", label: "Gestion par table", help: "Plusieurs additions ouvertes en parallèle — adapté à un bar, un café, un restaurant." },
  { value: "rendez_vous", label: "Service sur rendez-vous", help: "Vente liée à un créneau de planning plutôt qu'au stock — adapté à un coiffeur, un institut." },
];

export default function ShopSettingsPage({ embedded = false }) {
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const logoRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/shops");
    setShops(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setTestEmail(""); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nom: s.nom, type: s.type, adresse: s.adresse, code_postal: s.code_postal || "", ville: s.ville || "",
      telephone: s.telephone, siret: s.siret || "",
      tva_intracom: s.tva_intracom || "", forme_juridique: s.forme_juridique || "",
      capital_social: s.capital_social || "", rcs_ville: s.rcs_ville || "", tva_sur_debits: !!s.tva_sur_debits,
      acquisition_date: s.acquisition_date || "", usage_start_date: s.usage_start_date || "",
      usage_start_justification: s.usage_start_justification || "",
      ticket_impression_auto: !!s.ticket_impression_auto, activite_exemptee_agec: !!s.activite_exemptee_agec,
      vente_mode: s.vente_mode || "standard",
      caisse_mode: s.caisse_mode || "unique", nombre_caisses: s.nombre_caisses || 2,
      actif: s.actif !== false,
      smtp_host: s.smtp_host || "", smtp_port: s.smtp_port || "", smtp_secure: s.smtp_secure || "starttls",
      smtp_user: s.smtp_user || "", smtp_password: "", // ne jamais préremplir le mot de passe existant
      smtp_from_email: s.smtp_from_email || "", smtp_from_name: s.smtp_from_name || "",
    });
    setTestEmail("");
    setOpen(true);
  };

  const save = async () => {
    if (!editing && !form.ville.trim()) {
      toast.error("La ville est obligatoire pour créer une boutique");
      return;
    }
    try {
      // On n'envoie pas smtp_password s'il est vide lors d'une modif, pour ne pas écraser
      // le mot de passe déjà enregistré côté serveur.
      const payload = { ...form };
      if (editing && !payload.smtp_password) delete payload.smtp_password;
      if (editing) await api.patch(`/shops/${editing.id}`, payload);
      else await api.post("/shops", payload);
      toast.success("Boutique enregistrée");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendTestMail = async () => {
    if (!editing) { toast.error("Enregistrez d'abord la boutique avant de tester l'envoi"); return; }
    if (!testEmail) { toast.error("Saisissez une adresse e-mail de destination"); return; }
    setTesting(true);
    try {
      await api.post(`/shops/${editing.id}/mail-test`, { to_email: testEmail });
      toast.success(`E-mail de test envoyé à ${testEmail}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const uploadLogo = async (shopId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/shops/${shopId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Logo mis à jour");
    load();
  };

  const body = (
    <>
      <div className="flex justify-end mb-5">
        <Button data-testid="shop-add-button" onClick={openCreate} className="gap-2"><Plus size={16} />Nouvelle boutique/dépôt</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="shop-list">
        {shops.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid={`shop-card-${s.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {s.logo_url ? <img src={`${UPLOADS_BASE}${s.logo_url}`} className="w-12 h-12 object-contain rounded-md bg-secondary" /> : <div className="w-12 h-12 rounded-md bg-secondary" />}
                <div>
                  <p className="font-heading font-semibold flex items-center gap-2">
                    {s.nom}
                    {s.actif === false && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-500/15 text-slate-500 font-medium uppercase tracking-wide">Inactive</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">{s.type}</p>
                </div>
              </div>
              <button data-testid={`shop-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil size={15} /></button>
            </div>
            <p className="text-sm text-muted-foreground">{s.adresse}</p>
            {(s.code_postal || s.ville) && (
              <p className="text-sm text-muted-foreground">{[s.code_postal, s.ville].filter(Boolean).join(" ")}</p>
            )}
            <p className="text-sm text-muted-foreground">Tél: {s.telephone}</p>
            {s.siret && <p className="text-xs text-muted-foreground mb-2">SIRET: {s.siret}</p>}
            <input type="file" accept="image/*" className="hidden" ref={(el) => (logoRefs.current[s.id] = el)} onChange={(e) => e.target.files[0] && uploadLogo(s.id, e.target.files[0])} data-testid={`shop-logo-input-${s.id}`} />
            <Button size="sm" variant="outline" className="gap-2" onClick={() => logoRefs.current[s.id].click()} data-testid={`shop-logo-button-${s.id}`}><Upload size={14} />Changer le logo</Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shop-form-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvelle boutique/dépôt"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="shop-form-nom" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={!!editing}>
              <SelectTrigger data-testid="shop-form-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="boutique">Boutique</SelectItem><SelectItem value="depot">Dépôt</SelectItem></SelectContent>
            </Select>
            <Input data-testid="shop-form-adresse" placeholder="Adresse (rue, n°)" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
            <div className="grid grid-cols-3 gap-3">
              <Input data-testid="shop-form-code-postal" placeholder="Code postal" className="col-span-1" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} />
              <Input
                data-testid="shop-form-ville"
                placeholder={editing ? "Ville" : "Ville *"}
                className="col-span-2"
                value={form.ville}
                onChange={(e) => setForm({ ...form, ville: e.target.value })}
              />
            </div>
            {!editing && (
              <p className="text-xs text-muted-foreground -mt-1" data-testid="shop-form-ville-hint">
                Obligatoire : sert de base au code boutique utilisé dans la numérotation des tickets/factures, qui reste
                ensuite définitif pour cette boutique même si la ville est corrigée plus tard.
              </p>
            )}
            <Input data-testid="shop-form-telephone" placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
            <Input data-testid="shop-form-siret" placeholder="N° SIRET" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Scale size={15} />Mentions légales (factures)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Obligatoires sur toute facture dépassant 150 € HT ou concernant une société (art. 242 nonies A du CGI).
                Laissez vide si non applicable (ex : entrepreneur individuel sans capital social).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="shop-form-tva-intracom" placeholder="N° TVA intracommunautaire" value={form.tva_intracom} onChange={(e) => setForm({ ...form, tva_intracom: e.target.value })} />
                <Input data-testid="shop-form-rcs-ville" placeholder="Ville du RCS" value={form.rcs_ville} onChange={(e) => setForm({ ...form, rcs_ville: e.target.value })} />
                <Input data-testid="shop-form-forme-juridique" placeholder="Forme juridique (ex: SARL)" value={form.forme_juridique} onChange={(e) => setForm({ ...form, forme_juridique: e.target.value })} />
                <Input data-testid="shop-form-capital-social" placeholder="Capital social (ex: 10 000 €)" value={form.capital_social} onChange={(e) => setForm({ ...form, capital_social: e.target.value })} />
              </div>
              <label className="flex items-center gap-2.5 mt-3 cursor-pointer" htmlFor="shop-form-tva-debits">
                <Checkbox id="shop-form-tva-debits" data-testid="shop-form-tva-debits" checked={form.tva_sur_debits} onCheckedChange={(v) => setForm({ ...form, tva_sur_debits: !!v })} />
                <span className="text-sm">Option pour le paiement de la TVA d'après les débits</span>
              </label>
            </div>

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Scale size={15} />Impression du ticket de caisse (loi AGEC)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Depuis le 1ᵉʳ août 2023 (art. L541-15-10 du code de l'environnement), le ticket de caisse ne doit
                plus être imprimé/remis automatiquement à chaque vente — seulement à la demande du client, sauf
                activité exemptée (hôtellerie-restauration notamment).
              </p>
              <label className="flex items-center gap-2.5 cursor-pointer" htmlFor="shop-form-agec-exempt">
                <Checkbox id="shop-form-agec-exempt" data-testid="shop-form-agec-exempt" checked={form.activite_exemptee_agec} onCheckedChange={(v) => setForm({ ...form, activite_exemptee_agec: !!v, ticket_impression_auto: !!v || form.ticket_impression_auto })} />
                <span className="text-sm">Activité exemptée par la loi (hôtellerie, restauration...)</span>
              </label>
              <label className="flex items-center gap-2.5 mt-2 cursor-pointer" htmlFor="shop-form-ticket-auto">
                <Checkbox
                  id="shop-form-ticket-auto"
                  data-testid="shop-form-ticket-auto"
                  checked={form.ticket_impression_auto}
                  disabled={form.activite_exemptee_agec}
                  onCheckedChange={(v) => setForm({ ...form, ticket_impression_auto: !!v })}
                />
                <span className="text-sm">Ouvrir/imprimer le ticket automatiquement après chaque vente</span>
              </label>
              {!form.ticket_impression_auto && !form.activite_exemptee_agec && (
                <p className="text-xs text-muted-foreground mt-1">
                  Réglage recommandé par défaut : le vendeur affichera un bouton « Imprimer » à la caisse, à
                  utiliser seulement si le client le demande.
                </p>
              )}
            </div>

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold mb-1">Modèle de l'onglet Vente</p>
              <p className="text-xs text-muted-foreground mb-3">
                Adapte la présentation de l'onglet Vente au type d'activité de cette boutique. Le paiement, la
                TVA, les avoirs et la fidélité fonctionnent à l'identique quel que soit le modèle choisi.
              </p>
              <Select value={form.vente_mode} onValueChange={(v) => setForm({ ...form, vente_mode: v })}>
                <SelectTrigger data-testid="shop-form-vente-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VENTE_MODE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                {VENTE_MODE_OPTIONS.find((m) => m.value === form.vente_mode)?.help}
              </p>
            </div>

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold mb-1">Fond de caisse</p>
              <p className="text-xs text-muted-foreground mb-3">
                "Caisse unique" : un seul fond de caisse partagé par toute la boutique (comportement historique).
                "Plusieurs caisses" : chaque caisse physique (caisse 1, caisse 2...) a son propre fond de caisse,
                ouvert/fermé indépendamment, avec son propre journal.
              </p>
              <Select value={form.caisse_mode} onValueChange={(v) => setForm({ ...form, caisse_mode: v })}>
                <SelectTrigger data-testid="shop-form-caisse-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unique">Caisse unique</SelectItem>
                  <SelectItem value="multiple">Plusieurs caisses</SelectItem>
                </SelectContent>
              </Select>
              {form.caisse_mode === "multiple" && (
                <div className="mt-3 space-y-1">
                  <label className="text-xs text-muted-foreground">Nombre de caisses (1 à 9)</label>
                  <Input
                    data-testid="shop-form-nombre-caisses"
                    type="number" min={1} max={9} step={1}
                    value={form.nombre_caisses}
                    onChange={(e) => setForm({ ...form, nombre_caisses: Math.max(1, Math.min(9, Number(e.target.value) || 1)) })}
                    className="w-24"
                  />
                </div>
              )}
            </div>

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Scale size={15} />Attestation individuelle de conformité (Volet 2)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Date d'acquisition du logiciel/système de caisse et date de début d'utilisation effective par
                <strong> cette boutique</strong> — figurent dans l'attestation de conformité (Administrateurs &gt;
                Conformité fiscale). Facultatif, ne jamais deviner si inconnu.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Date d'acquisition</label>
                  <Input type="date" data-testid="shop-form-acquisition-date" value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Début d'utilisation</label>
                  <Input type="date" data-testid="shop-form-usage-start-date" value={form.usage_start_date} onChange={(e) => setForm({ ...form, usage_start_date: e.target.value })} />
                </div>
              </div>
              {form.usage_start_date && form.acquisition_date && form.usage_start_date < form.acquisition_date && (
                <Input
                  data-testid="shop-form-usage-justification"
                  className="mt-2"
                  placeholder="Justification obligatoire : utilisation antérieure à l'acquisition"
                  value={form.usage_start_justification}
                  onChange={(e) => setForm({ ...form, usage_start_justification: e.target.value })}
                />
              )}
            </div>

            {editing && (
              <div className="pt-3 mt-3 border-t border-border">
                <label className="flex items-center gap-2.5 cursor-pointer" htmlFor="shop-form-actif">
                  <Checkbox id="shop-form-actif" data-testid="shop-form-actif" checked={form.actif !== false} onCheckedChange={(v) => setForm({ ...form, actif: !!v })} />
                  <span className="text-sm">Boutique active</span>
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Une boutique ayant déjà émis des tickets/factures ne peut pas être supprimée (conservation légale
                  du registre fiscal) : désactivez-la ici pour la retirer des sélecteurs sans perdre son historique.
                </p>
              </div>
            )}

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Mail size={15} />Envoi d'e-mails (SMTP Infomaniak)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Ces identifiants permettent d'envoyer les devis par e-mail au client depuis
                votre propre domaine (ex: no-reply@votre-domaine.fr). Disponibles dans votre
                Manager Infomaniak &gt; Mail &gt; Configurer un logiciel de messagerie.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="shop-form-smtp-host" placeholder="Serveur SMTP (ex: mail.infomaniak.com)" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} />
                <Select value={form.smtp_secure} onValueChange={(v) => setForm({ ...form, smtp_secure: v, smtp_port: form.smtp_port || (v === "ssl" ? "465" : "587") })}>
                  <SelectTrigger data-testid="shop-form-smtp-secure"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
                    <SelectItem value="ssl">SSL/TLS (port 465)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input className="mt-3" data-testid="shop-form-smtp-port" placeholder="Port (587 ou 465)" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} />
              <Input className="mt-3" data-testid="shop-form-smtp-user" placeholder="Identifiant SMTP (adresse e-mail complète)" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} />
              <Input
                className="mt-3" type="password" data-testid="shop-form-smtp-password"
                placeholder={editing?.smtp_password_set ? "Mot de passe (laisser vide pour conserver l'actuel)" : "Mot de passe SMTP"}
                value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              />
              <Input className="mt-3" data-testid="shop-form-smtp-from-email" placeholder="Adresse d'expédition (ex: no-reply@votre-domaine.fr)" value={form.smtp_from_email} onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })} />
              <Input className="mt-3" data-testid="shop-form-smtp-from-name" placeholder="Nom d'expéditeur affiché (ex: Sogotech)" value={form.smtp_from_name} onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} />

              {editing && (
                <div className="flex items-center gap-2 mt-4">
                  <Input placeholder="Envoyer un e-mail de test à..." value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="shop-form-smtp-test-email" />
                  <Button type="button" variant="outline" size="sm" className="gap-2 whitespace-nowrap" disabled={testing} onClick={sendTestMail} data-testid="shop-form-smtp-test-button">
                    <Send size={14} />{testing ? "Envoi..." : "Tester"}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button data-testid="shop-form-save" onClick={save}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return body;
  return <Layout title="Boutiques &amp; Dépôts">{body}</Layout>;
}
